import noop from 'lodash/noop'
import { constants as ethersConstants } from 'ethers'
import { useEffect, useRef } from 'react'

import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useSWR } from 'modules/network/hooks/useSwr'

import { fetcherStandard } from 'modules/network/utils/fetcherStandard'
import { ContractNFCS } from 'modules/contracts/contracts'
import { NFCSInfo } from '../types'
import { CS_API_URL } from 'config'

type Args = {
  address: string | null | undefined
  shouldRevalidate?: boolean | null | undefined
}

export function useNFCSInfo({ address, shouldRevalidate }: Args) {
  const { chainId, walletAddress: walletAddressNetwork } = useWeb3()
  const walletAddress = address || walletAddressNetwork

  const { data, error, isLoading, mutate } = useSWR<NFCSInfo | null>(
    Boolean(walletAddress) ? `${walletAddress}-score-${chainId}` : null,
    async () => {
      if (!walletAddress) return null

      let tokenParsed: number
      let isPrimaryAddress: boolean | null = null
      let primaryBundleAddress: string | null = null
      let nfcsBundle: string[] = []
      try {
        const contract = ContractNFCS.connectRpc({ chainId })
        primaryBundleAddress = await contract.getPrimaryAddress(walletAddress)
        if (primaryBundleAddress === ethersConstants.AddressZero) {
          throw Error('NFCS not found')
        }
        isPrimaryAddress = walletAddress === primaryBundleAddress
        const tokenId = await contract.getToken(primaryBundleAddress)
        nfcsBundle = await contract.getBundle(tokenId)
        tokenParsed = tokenId.toNumber()
      } catch (err) {
        return null
      }

      try {
        const scoreData: {
          creditScore: number
          id: number
        } = await fetcherStandard(`${CS_API_URL}/score/${tokenParsed}`)
        return {
          tokenId: tokenParsed,
          isPrimaryAddress,
          primaryBundleAddress,
          nfcs: scoreData.creditScore,
          nfcsBundle,
        }
      } catch (err) {
        throw err
      }
    },
    {
      onError: noop,
      fallbackData: {
        tokenId: null,
        notStarted: true,
        isPrimaryAddress: null,
        primaryBundleAddress: null,
        nfcs: -1,
        nfcsBundle: [],
      },
      shouldRetryOnError: false,
      errorRetryCount: 0,
      revalidateOnMount: true,
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: latestData => {
        if (shouldRevalidate && !latestData?.nfcs) {
          return 5000
        }

        return 0
      },
    },
  )

  const { current: backoff } = useRef({ try: 1 })
  useEffect(() => {
    if (error?.status === 429) {
      let timeout: any

      const doBackoff = () => {
        timeout = setTimeout(() => {
          backoff.try = Math.min(backoff.try + 1, 60)
          mutate()
          doBackoff()
        }, backoff.try ** 2 * 1000)
      }

      doBackoff()

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [backoff, mutate, error?.status])

  return {
    nfcs: data?.nfcs ?? -1,
    tokenId: data?.tokenId,
    isPrimaryAddress: data?.isPrimaryAddress,
    primaryBundleAddress: data?.primaryBundleAddress,
    nfcsBundle: data?.nfcsBundle ?? [],
    error,
    isLoading: isLoading || data?.notStarted,
    mutate,
  }
}
